/**
 * File Explorer state — persists tree expansion + selection so the
 * panel survives reloads.
 *
 * Stored fields:
 *   - `expanded` — set of directory paths the user has opened.
 *   - `selectedPath` — last clicked file/dir (drives the right-pane viewer).
 *   - `currentRoot` — which scope root the tree is anchored at.
 *
 * Two non-persisted slices live alongside:
 *   - `picker` — when the founder clicks the "📎 Attach" button inside
 *     a card's Chat tab, the panel enters picker mode and the
 *     `resolvePick` callback is queued. Tree clicks then forward the
 *     selected path back instead of opening the viewer.
 *   - `pendingUpload` — drag-drop progress meter for the active panel.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface PickerSession {
  /** When set, tree clicks resolve with the picked path. */
  resolve: ((path: string | null) => void) | null;
}

interface UploadStatus {
  filename: string;
  loaded: number;
  total: number;
  error?: string | undefined;
}

interface FileExplorerSlice {
  // Persisted
  expanded: Record<string, true>;
  selectedPath: string | null;
  currentRoot: string | null;

  // Ephemeral
  picker: PickerSession;
  uploads: Record<string, UploadStatus>;

  // Actions
  toggleExpanded: (path: string) => void;
  setExpanded: (path: string, value: boolean) => void;
  selectPath: (path: string | null) => void;
  setCurrentRoot: (root: string | null) => void;

  openPicker: (resolve: (path: string | null) => void) => void;
  pickResult: (path: string | null) => void;
  cancelPicker: () => void;

  trackUpload: (id: string, status: UploadStatus) => void;
  clearUpload: (id: string) => void;
}

export const useFileExplorerStore = create<FileExplorerSlice>()(
  persist(
    (set, get) => ({
      expanded: {},
      selectedPath: null,
      currentRoot: null,

      picker: { resolve: null },
      uploads: {},

      toggleExpanded: (path) =>
        set((state) => {
          const next = { ...state.expanded };
          if (next[path]) delete next[path];
          else next[path] = true;
          return { expanded: next };
        }),

      setExpanded: (path, value) =>
        set((state) => {
          const next = { ...state.expanded };
          if (value) next[path] = true;
          else delete next[path];
          return { expanded: next };
        }),

      selectPath: (path) => set({ selectedPath: path }),
      setCurrentRoot: (root) => set({ currentRoot: root }),

      openPicker: (resolve) => set({ picker: { resolve } }),
      pickResult: (path) => {
        const { picker } = get();
        if (picker.resolve) picker.resolve(path);
        set({ picker: { resolve: null } });
      },
      cancelPicker: () => {
        const { picker } = get();
        if (picker.resolve) picker.resolve(null);
        set({ picker: { resolve: null } });
      },

      trackUpload: (id, status) =>
        set((state) => ({ uploads: { ...state.uploads, [id]: status } })),

      clearUpload: (id) =>
        set((state) => {
          const next = { ...state.uploads };
          delete next[id];
          return { uploads: next };
        }),
    }),
    {
      name: 'chaos-file-explorer',
      partialize: (state) => ({
        expanded: state.expanded,
        selectedPath: state.selectedPath,
        currentRoot: state.currentRoot,
      }),
    },
  ),
);
